import {MuiThemeProvider} from '@material-ui/core/styles';
import React, {createContext, ReactNode, useCallback, useContext, useEffect, useState} from 'react';
import {darkTheme, lightTheme} from './Theme';

type Props = {
    children : ReactNode;
};

type Mode = 'light' | 'dark';

type ModeState = {
    mode : Mode,
    setMode : (mode : Mode) => void;
};

const ModeStateContext = createContext<ModeState | null>(null);

const ThemeProvider = ({children} : Props) : React.ReactElement => {
    const [mode, setMode] = useState<Mode>(() => {
        const preferredMode = window.localStorage.getItem('preferred-mode') as Mode || undefined;

        if (preferredMode && ['light', 'dark'].includes(preferredMode)) {
            return preferredMode;
        }

        if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }

        return 'light';
    });

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (event : MediaQueryListEvent) => {
            if (!window.localStorage.getItem('preferred-mode')) {
                setMode(event.matches ? 'dark' : 'light');
            }
        };
        mediaQuery.addEventListener('change', handleChange);

        return () => {
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, []);

    const handleModeChange = useCallback((mode : Mode) => {
        const systemMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

        if (systemMode === mode) {
            window.localStorage.removeItem('preferred-mode');
        } else {
            window.localStorage.setItem('preferred-mode', mode);
        }

        setMode(mode);
    }, []);

    const theme = mode === 'light' ? lightTheme : darkTheme;

    return (
        <MuiThemeProvider theme={theme}>
            <ModeStateContext.Provider value={{mode, setMode: handleModeChange}}>
                {children}
            </ModeStateContext.Provider>
        </MuiThemeProvider>
    );
};

export const useModeState = () : ModeState => {
    const context = useContext(ModeStateContext);

    if (!context) {
        throw new Error('Context was used before initial load');
    }

    return context;
};

export default ThemeProvider;
